/**
 * connection.ts — first-class connectivity tracking (Responsive
 * Workstream C, roam-responsive-pwa-spec.md §8 / §1.11).
 *
 * `navigator.onLine` ALONE is unreliable: it reflects only the OS
 * network-interface state, so a phone behind a captive portal, or on
 * wifi with no real route out, still reports `true`. This combines
 * three signals instead:
 *
 *   1. The browser's `online`/`offline` events (cheap, immediate).
 *   2. A same-origin confirmation ping fired the moment `online` fires
 *      — the interface came back, but is the connection REAL?
 *   3. Real request outcomes fed in by services/api.ts: a network-level
 *      fetch failure marks us offline immediately (no need to wait for
 *      the browser to notice, which can lag on flaky wifi); a
 *      successful response — of ANY HTTP status, even a 404 — proves
 *      we're connected and clears a stale offline flag the browser's
 *      own event might have missed (e.g. a captive portal that starts
 *      routing real traffic without ever firing `online` again).
 *
 * There is deliberately no offline write queue here (spec §1.11: no
 * offline mode at all) — this is purely "notice, and say so clearly",
 * feeding cos-app-shell's banner and api.ts's ApiError.retriableOffline
 * marking.
 */

export type ConnectionListener = (online: boolean) => void;

// Exported (not just the singleton below) so tests can construct
// isolated instances rather than fighting the singleton's
// construction-time navigator.onLine read and its one shared
// listener set.
export class ConnectionMonitor {
	private online: boolean = typeof navigator === "undefined" ? true : navigator.onLine;
	private listeners = new Set<ConnectionListener>();
	private confirming = false;

	constructor() {
		if (typeof window === "undefined") return;
		window.addEventListener("online", this._onBrowserOnline);
		window.addEventListener("offline", this._onBrowserOffline);
	}

	private _onBrowserOnline = (): void => {
		void this._confirmReconnect();
	};

	private _onBrowserOffline = (): void => {
		this._setOnline(false);
	};

	/** Removes the window listeners. The app-lifetime singleton below
	 *  never calls this; it exists so tests can construct scoped
	 *  instances without leaking listeners across cases. */
	dispose(): void {
		if (typeof window === "undefined") return;
		window.removeEventListener("online", this._onBrowserOnline);
		window.removeEventListener("offline", this._onBrowserOffline);
	}

	private async _confirmReconnect(): Promise<void> {
		if (this.confirming) return;
		this.confirming = true;
		try {
			const res = await fetch("/health/live", { cache: "no-store" });
			this._setOnline(res.ok);
		} catch {
			// The interface says online; a real request still can't get
			// through. Stay marked offline until one succeeds.
			this._setOnline(false);
		} finally {
			this.confirming = false;
		}
	}

	/** A mutating (or any) API call's fetch() itself rejected — the
	 *  clearest possible offline signal, reported directly by api.ts. */
	reportNetworkFailure(): void {
		this._setOnline(false);
	}

	/** Any API response arrived, any HTTP status — proves the
	 *  connection is real. Called by api.ts after every fetch that
	 *  didn't throw. */
	reportSuccess(): void {
		this._setOnline(true);
	}

	isOnline(): boolean {
		return this.online;
	}

	/** Subscribe to online/offline transitions. Returns an unsubscribe
	 *  function — callers (cos-app-shell) unsubscribe in
	 *  disconnectedCallback, mirroring every other subscription in that
	 *  component. */
	subscribe(listener: ConnectionListener): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	private _setOnline(next: boolean): void {
		if (next === this.online) return;
		this.online = next;
		for (const l of this.listeners) l(next);
	}
}

export const connectionMonitor = new ConnectionMonitor();
